import React, { useState } from "react";
import { Alert, Button, CircularProgress, IconButton, Snackbar, Tooltip } from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "react-query";
import dayjs from "dayjs";
import { getGoogleCalendarStatus } from "../../api/client";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { runGoogleCalendarSyncToCompletion } from "../../utils/googleCalendarSync";
import ApiErrorAlertBody, { resolveApiErrorNotice } from "../../components/ApiErrorAlertBody";

const SESSION_KEY = "agenda-google-sync-at";

function unwrapStatus(res) {
  if (res && typeof res === "object" && "data" in res) return res.data;
  return res;
}

export default function AgendaGoogleSyncButton({ compact = false }) {
  const { t } = useTranslation("teamAgenda");
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState("");
  const [actionNotice, setActionNotice] = useState(null);
  const [progress, setProgress] = useState(null);

  const { data: statusRaw, isLoading: statusLoading } = useQuery(
    ["google-calendar-status"],
    async () => {
      const r = await getGoogleCalendarStatus();
      return unwrapStatus(r) ?? {};
    },
    { staleTime: 15_000 }
  );

  const status = statusRaw || {};
  const visible = Boolean(status.connected && status.syncEnabled);
  const needsReauth = Boolean(status.needsReauth);
  const remoteSyncing = Boolean(status.syncing);

  const syncMutation = useMutation(
    async ({ forceFullSync = false } = {}) => {
      setActionError("");
      setActionNotice(null);
      return runGoogleCalendarSyncToCompletion({
        forceFullSync,
        onChunk: (chunk) => {
          const count = Number(chunk?.processed) || Number(chunk?.syncProcessed) || 0;
          setProgress({ count, syncing: chunk?.syncing || chunk?.complete === false });
          queryClient.invalidateQueries(["user-agenda"]);
        },
      });
    },
    {
      onSuccess: (result) => {
        sessionStorage.setItem(SESSION_KEY, String(Date.now()));
        queryClient.invalidateQueries(["google-calendar-status"]);
        queryClient.invalidateQueries(["user-agenda"]);
        if (result?.background) {
          setProgress({
            count: Number(result.processed) || 0,
            syncing: true,
            background: true,
          });
          return;
        }
        setProgress(null);
      },
      onError: (err) => {
        setProgress(null);
        const code = err?.code || err?.response?.data?.code || err?.data?.code;
        if (code === "google_calendar_needs_reauth") {
          queryClient.invalidateQueries(["google-calendar-status"]);
          const message = t("googleSync.errors.needsReauth");
          setActionError(message);
          setActionNotice({ message, action: null });
          return;
        }
        const notice = resolveApiErrorNotice(err, t("googleSync.errors.syncFailed"));
        setActionError(getApiErrorMessage(err, notice.message));
        setActionNotice({
          message: notice.message,
          action: notice.action || t("googleSync.errors.retry"),
        });
      },
    }
  );

  if (statusLoading || !visible) return null;

  const lastSyncLabel = status.lastSyncAt
    ? dayjs(String(status.lastSyncAt).replace(" ", "T")).format("DD/MM HH:mm")
    : null;
  const syncing = syncMutation.isLoading || remoteSyncing || Boolean(progress?.syncing);
  const progressCount = Number(progress?.count) || Number(status.syncProcessed) || 0;

  const tooltip = needsReauth
    ? t("googleSync.errors.needsReauth")
    : syncing
    ? t("googleSync.syncingHint", { count: progressCount })
    : actionError
    ? actionError
    : lastSyncLabel
    ? t("googleSync.lastSync", { date: lastSyncLabel })
    : t("googleSync.syncNow");

  const snackbar = (
    <Snackbar
      open={Boolean(actionNotice?.message)}
      autoHideDuration={12000}
      onClose={() => setActionNotice(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        severity="error"
        variant="filled"
        onClose={() => setActionNotice(null)}
        sx={{ width: "100%", maxWidth: 520 }}
      >
        <ApiErrorAlertBody message={actionNotice?.message} action={actionNotice?.action} />
      </Alert>
    </Snackbar>
  );

  if (compact) {
    return (
      <>
        <Tooltip title={tooltip}>
          <span>
            <IconButton
              size="small"
              onClick={() => syncMutation.mutate({ forceFullSync: true })}
              disabled={syncMutation.isLoading || needsReauth}
              aria-label={t("googleSync.syncNow")}
              color={actionError || needsReauth ? "error" : "default"}
            >
              {syncing ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {snackbar}
      </>
    );
  }

  return (
    <>
      <Tooltip title={tooltip}>
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={() => syncMutation.mutate({ forceFullSync: true })}
            disabled={syncMutation.isLoading || needsReauth}
            color={actionError || needsReauth ? "error" : "primary"}
            sx={{ textTransform: "none", fontWeight: 500, whiteSpace: "nowrap" }}
          >
            {syncing ? t("googleSync.syncing") : t("googleSync.syncNow")}
          </Button>
        </span>
      </Tooltip>
      {snackbar}
    </>
  );
}
